import React, { useState } from 'react';
import { Box, Typography, Tabs, Tab, useTheme, useMediaQuery } from '@mui/material';
import { UsersList } from '../components/users/UsersList';
import { RolesList } from '../components/users/RolesList';
import { UserDialog } from '../components/users/UserDialog';
import { RoleDialog } from '../components/users/RoleDialog';
import { User, Role } from '../types';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box>{children}</Box>}
  </div>
);

export const Users: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleUserEdit = (user: User) => {
    setSelectedUser(user);
    setUserDialogOpen(true);
  };

  const handleRoleEdit = (role: Role) => {
    setSelectedRole(role);
    setRoleDialogOpen(true);
  };

  const handleUserSave = (userData: Partial<User>) => {
    console.log('Saving user:', userData);
    setUserDialogOpen(false);
    setSelectedUser(null);
  };

  const handleRoleSave = (roleData: Partial<Role>) => {
    console.log('Saving role:', roleData);
    setRoleDialogOpen(false);
    setSelectedRole(null);
  };

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        gap: { xs: 2, sm: 0 },
        mb: 4,
      }}>
        <Typography variant="h4" sx={{ flex: 1 }}>Users & Roles</Typography>
        <Tabs 
          value={activeTab} 
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant={isMobile ? "fullWidth" : "standard"}
          sx={{
            width: { xs: '100%', sm: 'auto' },
            '& .MuiTabs-flexContainer': {
              gap: { xs: 0, sm: 2 },
            },
          }}
        >
          <Tab 
            label="Users" 
            sx={{ 
              minWidth: { xs: 0, sm: 'auto' },
              px: { xs: 2, sm: 3 },
            }}
          />
          <Tab 
            label="Roles" 
            sx={{ 
              minWidth: { xs: 0, sm: 'auto' },
              px: { xs: 2, sm: 3 },
            }}
          />
        </Tabs>
      </Box>

      <TabPanel value={activeTab} index={0}>
        <UsersList
          onUserEdit={handleUserEdit}
          onAddUser={() => setUserDialogOpen(true)}
          isMobile={isMobile}
        />
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <RolesList
          onRoleEdit={handleRoleEdit}
          onAddRole={() => setRoleDialogOpen(true)}
          isMobile={isMobile}
        />
      </TabPanel>

      <UserDialog
        open={userDialogOpen}
        onClose={() => {
          setUserDialogOpen(false);
          setSelectedUser(null);
        }}
        onSave={handleUserSave}
        user={selectedUser}
      />

      <RoleDialog
        open={roleDialogOpen}
        onClose={() => {
          setRoleDialogOpen(false);
          setSelectedRole(null);
        }}
        onSave={handleRoleSave}
        role={selectedRole}
      />
    </Box>
  );
};